// =============================================================================
// EXPORT WORKFLOW .JSON (issue #41, RFC #34)
// Sérialise le canvas complet (blocs + wires + bibliothèque de prompts
// embarquée) vers le format JSON partageable `rabbit-prompt-workflow`.
// =============================================================================

import type { Block, Wire, ProjectPrompt } from '../types';
import {
  PromptWorkflow,
  WORKFLOW_FORMAT,
  WORKFLOW_VERSION,
} from '../types/workflow';
import { isStandardSlotType } from '../constants';

// Blocs éphémères exclus de l'export (même filtrage que la persistence,
// cf. useCanvasPersistence.ts — ces blocs sont des panneaux, pas du contenu).
// « character-lora » est exclu aussi (revue PO) : son characterId est propre
// au compte — importé ailleurs, le bloc pointerait vers un personnage
// inexistant (conflit) ; les wires orphelins tombent avec (étape 2).
const EXCLUDED_BLOCK_TYPES = new Set<Block['type']>([
  'generate',
  'prompt-length',
  'prompt-puzzle',
  'character-lora',
]);

// Slot personnel (issue #66) ? Exclu de l'export (revue PO) : son
// connectorType est l'ID d'un doc customConnectors propre au compte —
// importé ailleurs, le bloc référencerait les userPrompts d'un autre
// utilisateur (il faudrait un mécanisme « request access to the owner »,
// volontairement reporté). L'export ne garde que les slots natifs et
// les blocs autonomes (freetext, sticky-note, detailers…).
const isCustomSlotBlock = (block: Block): boolean =>
  (block.isConnector || block.type === 'connector') &&
  !isStandardSlotType(block.connectorType);

// Flags transitoires d'animation, jamais exportés
const TRANSIENT_BLOCK_FLAGS = ['isNew', 'animationDelay', 'layoutTransition'] as const;
const TRANSIENT_WIRE_FLAGS = ['isNew', 'animationDelay'] as const;

/** Retire les flags d'animation d'un bloc (partagé avec l'import). */
export const stripTransientBlockFlags = (block: Block): Block => {
  const cleaned = { ...block };
  for (const flag of TRANSIENT_BLOCK_FLAGS) {
    delete cleaned[flag];
  }
  return cleaned;
};

/** Retire les flags d'animation d'un wire (partagé avec l'import). */
export const stripTransientWireFlags = (wire: Wire): Wire => {
  const cleaned = { ...wire };
  for (const flag of TRANSIENT_WIRE_FLAGS) {
    delete cleaned[flag];
  }
  return cleaned;
};

/**
 * Construit le workflow JSON à partir de l'état du canvas.
 * Fonction pure — testable sans React ni Firebase.
 *
 * @param name Nom du workflow (metadata), typiquement le nom du projet.
 * @param promptsByCategory Bibliothèque du projet : pour chaque bloc, toutes
 *                          les candidates de sa catégorie sont embarquées
 *                          (pas seulement la sélection — issue #45).
 */
export const exportWorkflow = (
  blocks: Block[],
  wires: Wire[],
  promptsByCategory: Record<string, ProjectPrompt[]>,
  name: string
): PromptWorkflow => {
  // 1. Filtre les blocs éphémères et les slots personnels, nettoie les
  //    flags transitoires
  const exportedBlocks = blocks
    .filter((b) => !EXCLUDED_BLOCK_TYPES.has(b.type) && !isCustomSlotBlock(b))
    .map(stripTransientBlockFlags);

  // 1bis. Droppe les fils d'un slot exclu (blocs personnels dont le
  //    type = ID d'un customConnectorBlock propre au compte — inutilisables
  //    ailleurs, ils tombent avec leur slot).
  const exportedIds = new Set(exportedBlocks.map((b) => b.id));
  const keptBlocks = exportedBlocks.filter(
    (b) => !b.parentConnectorId || exportedIds.has(b.parentConnectorId)
  );

  // 2. Droppe les wires orphelins (extrémité sur un bloc exclu)
  const blockIds = new Set(keptBlocks.map((b) => b.id));
  const exportedWires = wires
    .filter((w) => blockIds.has(w.from) && blockIds.has(w.to))
    .map(stripTransientWireFlags);

  // 3. Embarque TOUTES les prompts candidates des catégories présentes sur le
  //    canvas (dédupliquées par promptId) — pas seulement les sélections,
  //    sinon chaque bloc importé est réduit à une seule prompt (issue #45).
  //    addedAt est exclu : Timestamp Firestore non sérialisable proprement,
  //    et sans valeur à l'import.
  const embeddedPrompts: ProjectPrompt[] = [];
  const seen = new Set<string>();
  for (const block of keptBlocks) {
    const candidates = promptsByCategory[block.type] ?? [];
    for (const prompt of candidates) {
      if (seen.has(prompt.promptId)) continue;
      seen.add(prompt.promptId);
      embeddedPrompts.push({
        promptId: prompt.promptId,
        category: prompt.category,
        connector: prompt.connector,
        imageUrl: prompt.imageUrl,
        // Vignettes embarquées aussi (issue #66 : les prompts personnels sans
        // imageUrl ont parfois seulement une vignette — ou rien).
        thumbnailUrl: prompt.thumbnailUrl,
        canvasThumbnailUrl: prompt.canvasThumbnailUrl,
        prompt: prompt.prompt,
        tags: prompt.tags,
        weight: prompt.weight,
        // Type de logement + zones dérivées (prompts housing uniquement) :
        // embarqués pour que le bloc Zone survive au round-trip export/import.
        housingType: prompt.housingType,
        zones: prompt.zones,
      });
    }
  }

  return {
    version: WORKFLOW_VERSION,
    format: WORKFLOW_FORMAT,
    metadata: {
      name,
      createdAt: new Date().toISOString(),
    },
    blocks: keptBlocks,
    wires: exportedWires,
    prompts: embeddedPrompts,
  };
};

/**
 * Sérialise le workflow en JSON lisible (partage copier-coller / diff).
 * Les clés de chaque objet sont triées alphabétiquement : l'ordre d'insertion
 * hérité des constructeurs de blocs varie d'un chemin de code à l'autre, ce qui
 * rendait les exports impossibles à differ (cosmétique — l'import lit par clé).
 */
export const serializeWorkflow = (workflow: PromptWorkflow): string =>
  JSON.stringify(
    workflow,
    (_key, value) =>
      value && typeof value === 'object' && !Array.isArray(value)
        ? Object.keys(value)
            .sort()
            .reduce((sorted: Record<string, unknown>, k) => {
              sorted[k] = value[k];
              return sorted;
            }, {})
        : value,
    2
  );

/** Nom de fichier d'export : slug du nom de workflow + extension .json */
const toFileName = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'rabbit-workflow'}.json`;
};

/** Déclenche le téléchargement du workflow comme fichier .json. */
export const downloadWorkflow = (workflow: PromptWorkflow): void => {
  const blob = new Blob([serializeWorkflow(workflow)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = toFileName(workflow.metadata.name);
  anchor.click();
  URL.revokeObjectURL(url);
};
